"use client"

import { motion } from "framer-motion"
import { Search, Code2, Rocket, PenLine } from "lucide-react"
import { processSteps } from "@/features/home/data"
import { staggerContainer, staggerItem, fadeUp, sectionLabel } from "@/lib/motion"

const iconMap = { Search, Code2, Rocket, Figma: PenLine }

const VP = { once: true, amount: 0.05 }

export function ProcessSection() {
  return (
    <section id="surec" className="relative py-16 sm:py-24 lg:py-32" aria-label="Çalışma sürecimiz">
      <div className="brand-divider" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-20"
        >
          <motion.span variants={sectionLabel} className="text-primary font-medium text-sm uppercase tracking-wider inline-block">
            Nasıl Çalışırız?
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 sm:mb-6 text-foreground">
            Başarıya Giden <span className="text-gradient-brand">Yol Haritası</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Şeffaf, disiplinli ve müşteri odaklı sürecimizle her projeyi zamanında ve kaliteli teslim ediyoruz.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="hidden md:grid md:grid-cols-4 gap-0 relative"
        >
          <div className="absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-border to-transparent" aria-hidden="true" />

          {processSteps.map((step) => {
            const Icon = iconMap[step.iconName as keyof typeof iconMap] ?? Code2
            return (
              <motion.div key={step.number} variants={staggerItem} className="relative flex flex-col items-center text-center px-4">
                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center z-10 relative">
                    <Icon className="w-7 h-7 text-primary" aria-hidden="true" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center font-[family-name:var(--font-heading)]" aria-hidden="true">
                    {step.number}
                  </span>
                </div>
                <h3 className="font-[family-name:var(--font-heading)] text-base font-bold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            )
          })}
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="md:hidden space-y-0"
        >
          {processSteps.map((step, i) => {
            const Icon = iconMap[step.iconName as keyof typeof iconMap] ?? Code2
            const isLast = i === processSteps.length - 1
            return (
              <motion.div
                key={step.number}
                variants={staggerItem}
                className="relative flex gap-4"
              >
                <div className="flex flex-col items-center shrink-0">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                    </div>
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" aria-hidden="true">
                      {step.number}
                    </span>
                  </div>
                  {!isLast && (
                    <div className="w-px flex-1 bg-gradient-to-b from-primary/30 to-transparent mt-2 min-h-[2rem]" aria-hidden="true" />
                  )}
                </div>

                <div className={`glass hairline rounded-3xl p-5 flex-1 transition-all active:scale-[0.98] ${isLast ? 'mb-0' : 'mb-4'}`}>
                  <h3 className="font-[family-name:var(--font-heading)] text-base font-bold text-foreground mb-1">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

      </div>
    </section>
  )
}
